import { VoyageRoute } from "../models/voyage-routing-model";
import { VoyagePlan } from "../models/vessel-planning-model";
import { ImportResult } from "./types/converters.types";

export class VesselsPlanningConverter {
    static check(str: string) {
        try {
            var obj = JSON.parse(str);

            // Check that this is 'our' format.
            if (obj) {
                if (obj.routes && obj.routes.length > 0) {
                    // If it's an array, return the first item
                    return {route: obj.routes[0]};
                } else if (obj.route) {
                    // If it's a single route, return it
                    return obj;
                } else if (obj.route_geometry) {
                    // Also support the (old) raw dump from the route tester.
                    return {route: obj};
                }
            }
        } catch(e) {
            // don't care
        }
    }

    static import(str: string): ImportResult|undefined {
        try {
            const obj = VesselsPlanningConverter.check(str);

            if (!obj) return;

            const route: VoyageRoute = {
                route_geometry: obj.route.route_geometry,
                routeId: '', // check if if the routeId is needed
            };

            for (const feature of route.route_geometry)
            {
                // Remove eta (since it is tied to a specific date) and isPassed properties
                delete feature.properties.eta;
                delete feature.properties.isPassed;
                // Remove eta_deviation since we dont want to show old warnings
                delete feature.properties.eta_deviation;
            }

            if (!obj.version) obj.version = "1.0.0"; // Support version 1.0.0 prior to version stamp.

            // Verify that the version is "1.0.0" (when we bump versions, we should handle migrations)
            if (obj.version !== "1.0.0") return;

            if (!obj.route.passagePlan) {
                route.passagePlan = {
                    description: obj.passagePlan.description,
                    reminders: obj.passagePlan.reminders,
                    notEcdisCompliant: obj.passagePlan.notEcdisCompliant,
                    encCellsUsed: obj.passagePlan.encCellsUsed,
                    booksAndTables: obj.passagePlan.booksAndTables,
                    chartsAndLightsNotes: obj.passagePlan.chartsAndLightsNotes,
                    sailingDirections: obj.passagePlan.sailingDirections,
                    listOfRadioSignals: obj.passagePlan.listOfRadioSignals,
                    listOfLights: obj.passagePlan.listOfLights,
                    gpsEcdisRadarMap: obj.passagePlan.gpsEcdisRadarMap,
                    paperCharts: obj.passagePlan.paperCharts,
                    pubCorrectedToWeek: obj.passagePlan.pubCorrectedToWeek,
                    tideTables: obj.passagePlan.tideTables,
                    publicationsNotes: obj.passagePlan.publicationsNotes,
                    mastersOrders: obj.passagePlan.mastersOrders,
                    captain: obj.passagePlan.captain,
                    navigationalOfficer: obj.passagePlan.navigationalOfficer,
                    planDate: obj.passagePlan.planDate,
                    safetyMessages: {
                        navareaRegions: obj.passagePlan.safetyMessages.navareaRegions,
                        navtexStations: obj.passagePlan.safetyMessages.navtexStations
                    },
                    weatherInfo: {
                        navareaRegions: obj.passagePlan.weatherInfo.navareaRegions,
                        navtexStations: obj.passagePlan.weatherInfo.navtexStations,
                        info: obj.passagePlan.weatherInfo.info
                    },
                    equipmentStatus: {
                        operationalCondition: obj.passagePlan.equipmentStatus.operationalCondition,
                        failures: obj.passagePlan.equipmentStatus.failures
                    },
                    weatherRouting: {
                        required: false,            
                        acknowledged: false,     
                        pilotPointAcknowledged: false 
                    }
                }
            } else {
                route.passagePlan = obj.route.passagePlan;
            }

            // Import full passagePlan object as extras but don't include other fields from the plan.
            return {
                route,
                extras: {
                    passagePlan: obj.passagePlan
                }
            };
        } catch (error) {
            console.warn(error);
        }
    }

    static export(plan: VoyagePlan): string
    {
        // Bail out if no valid route is selected.
        if (!plan.currentSelectedRouteId)
        {
            throw new Error("No valid route selected in plan!");
        }

        const planCopy = JSON.parse(JSON.stringify(plan));

        // Stamp version.
        planCopy.version = "1.0.0";

        // Only include the selected route.
        const routeIndex = plan.routes.findIndex(route => route.routeId === plan.currentSelectedRouteId) ?? plan.selectedRouteIndex   
        planCopy.route = planCopy.routes[routeIndex];

        // Remove fields not to be exported.
        planCopy.routes = undefined;
        planCopy.selectedRouteIndex = undefined;

        return JSON.stringify(planCopy);
    }
}
